import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import type { ExportItem } from "app/pages/sales-intelligence/sub-modules/management/store/type";
import { ExportSettingsSourceType } from "app/pages/sales-intelligence/sub-modules/management/store/type";
import type { PlacementType } from "app/pages/sales-intelligence/types";

import {
    ADD_FIELD,
    PRE_DEFINED_CREATE_FIELD,
    PRE_DEFINED_FIELD,
    PRE_DEFINED_UPDATE_FIELD,
    TECHNOGRAPHICS_FIELD,
} from "./const";
import type { CrmFieldType } from "./types";

export const DD_OPEN_ON_TOP_LIMIT = 5;

export const chopTextOverTheLimit = (text: string, limit: number): string => {
    return text && text.length > limit ? `${text.substring(0, limit)}...` : text;
};

export const createPlacement = (
    tableLength: number,
    index: number,
    fieldName?: "technographics",
): { placement: PlacementType } => {
    if (fieldName === "technographics" && index < 5) {
        return { placement: "bottom-left" };
    }

    let placement: PlacementType = "ontop-left";

    const isLastTwo = index >= tableLength - 2;
    const hasMoreThanFiveItems = tableLength > DD_OPEN_ON_TOP_LIMIT;

    if (isLastTwo && hasMoreThanFiveItems) {
        placement = "top-left";
    }

    return { placement };
};

export const isPreDefinedField = ({ field, sourceType }: ExportItem): boolean => {
    if (!field) {
        return false;
    }

    if (
        field.includes(PRE_DEFINED_FIELD) ||
        sourceType === ExportSettingsSourceType.Predefined ||
        sourceType === ExportSettingsSourceType.PredefinedCreate ||
        sourceType === ExportSettingsSourceType.PredefinedUpdate
    ) {
        return true;
    }

    return false;
};

export const getPredefinedFieldBase = (field: string): string | null => {
    if (field.includes(PRE_DEFINED_CREATE_FIELD)) {
        return PRE_DEFINED_CREATE_FIELD;
    }
    if (field.includes(PRE_DEFINED_UPDATE_FIELD)) {
        return PRE_DEFINED_UPDATE_FIELD;
    }
    if (field.includes(PRE_DEFINED_FIELD)) {
        return PRE_DEFINED_FIELD;
    }

    return null;
};

export const isSamePredefinedFieldType = (fieldA: string, fieldB: string): boolean => {
    const baseA = getPredefinedFieldBase(fieldA);
    const baseB = getPredefinedFieldBase(fieldB);

    return baseA !== null && baseA === baseB;
};

export const isTechnographicsField = ({ field, sourceType }: ExportItem): boolean => {
    if (field.includes(TECHNOGRAPHICS_FIELD) || sourceType === ExportSettingsSourceType.Technologies) {
        return true;
    }

    return false;
};

export const isPickListField = (externalField: string, crmOptions: CrmFieldType[]): boolean => {
    return crmOptions.find(({ name }) => name === externalField)?.dataType === "Picklist";
};

export const getPredefinedMetadataOnCrmFieldChange = (
    newCrmField: string,
    oldExternalField: string,
    columns: CrmFieldType[],
): string | null => {
    const isNewSettingPicklist = isPickListField(newCrmField, columns);
    const isOldSettingPickList = isPickListField(oldExternalField, columns);

    if (
        (isNewSettingPicklist && !isOldSettingPickList) ||
        (isNewSettingPicklist && isOldSettingPickList && newCrmField !== oldExternalField)
    ) {
        const option = columns.find(({ name }) => name === newCrmField);

        return Object.keys(option?.pickListItems || {})[0] ?? "";
    }

    if (!isNewSettingPicklist && isOldSettingPickList) {
        return "";
    }

    return null;
};

export const getDefaultContactsCrmValue = (field: string, integrationName: CrmIntegrationProvider) => {
    if (field === "job_title") {
        if (integrationName === "salesforce") {
            return "Title";
        }
        return "Job title";
    }
    if (field === "direct_phone") {
        if (integrationName === "salesforce") {
            return "Phone";
        }
        return "Phone number";
    }
    return "";
};

export const getSwFieldColumnName = (setting: ExportItem): string => {
    if (isPreDefinedField(setting) || isTechnographicsField(setting)) {
        return setting.field;
    }

    return setting.metadata || setting.field;
};

export const isPrimaryMandatoryFieldInstance = (
    setting: ExportItem,
    tableData: ReadonlyArray<ExportItem>,
    mandatoryFieldKeys: readonly string[],
): boolean => {
    const metadata = setting.metadata ?? "";

    if (!metadata || !mandatoryFieldKeys.includes(metadata)) {
        return false;
    }

    const firstIndex = tableData.findIndex((row) => row.field !== ADD_FIELD && row.metadata === metadata);
    const settingIndex = tableData.indexOf(setting);

    return firstIndex >= 0 && firstIndex === settingIndex;
};

export const getUniqueFieldName = (
    fieldId: string,
    tableData: ReadonlyArray<{ field: string }>,
    currentSetting?: { field: string },
): { fieldName: string; metadata: string } => {
    const dataForIndex = currentSetting ? tableData.filter((row) => row.field !== currentSetting.field) : tableData;

    if (getPredefinedFieldBase(fieldId) || fieldId.includes(TECHNOGRAPHICS_FIELD)) {
        const index = calcIndex(fieldId, dataForIndex);
        return { fieldName: `${fieldId}_${index}`, metadata: "" };
    }

    const index = calcIndex(fieldId, dataForIndex);

    if (index === 0) {
        return { fieldName: fieldId, metadata: fieldId };
    }

    return { fieldName: `${fieldId}_${index}`, metadata: fieldId };
};

export const isSameIndexedFieldInstance = (field: string, fieldId: string): boolean => {
    if (field === fieldId) {
        return true;
    }

    if (!field.startsWith(`${fieldId}_`)) {
        return false;
    }

    const suffix = field.slice(fieldId.length + 1);

    return /^\d+$/.test(suffix);
};

export const calcIndex = (fieldName: string, tableData: ReadonlyArray<{ field: string }>): number => {
    const predefinedBase = getPredefinedFieldBase(fieldName);
    const matchFieldName = predefinedBase ?? fieldName;

    return tableData.reduce((acc, { field }) => {
        if (!field) {
            return acc;
        }

        const isMatch = predefinedBase
            ? isSamePredefinedFieldType(field, fieldName)
            : isSameIndexedFieldInstance(field, fieldName);

        if (!isMatch) {
            return acc;
        }

        const suffix = field.startsWith(`${matchFieldName}_`) ? field.slice(matchFieldName.length + 1) : "";
        const index = Number(suffix);

        return acc + (Number.isFinite(index) ? index + 1 : 1);
    }, 0);
};
